import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  DEFAULT_SCHEDULE_SETTINGS,
  getCachedScheduleSettings,
  loadUserPreferences,
  saveScheduleSettings,
} from '../../utils/userPreferences';

function Settings({ onClose }) {
  const [settings, setSettings] = useState(() => getCachedScheduleSettings());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadUserPreferences().then((prefs) => {
      if (!cancelled && prefs?.scheduleSettings) {
        setSettings({ ...DEFAULT_SCHEDULE_SETTINGS, ...prefs.scheduleSettings });
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleChange = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    setSaving(true);
    saveScheduleSettings(newSettings).finally(() => setSaving(false));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Schedule Settings</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Synced to your account — same on every browser or PC
              {saving ? ' · saving…' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Auto-add Schedule */}
          <div>
            <label className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Auto-add Schedule</p>
                <p className="text-xs text-gray-500">Automatically add schedules when job dates are confirmed</p>
              </div>
              <input
                type="checkbox"
                checked={settings.autoAddSchedule}
                onChange={(e) => handleChange('autoAddSchedule', e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
            </label>
          </div>

          {/* Ask Before Adding */}
          {settings.autoAddSchedule && (
            <div>
              <label className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Ask Before Adding</p>
                  <p className="text-xs text-gray-500">Show confirmation popup before auto-adding schedules</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.askBeforeAdding}
                  onChange={(e) => handleChange('askBeforeAdding', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
              </label>
            </div>
          )}

          {/* Suppress Popup */}
          {settings.autoAddSchedule && settings.askBeforeAdding && (
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Suppress Popup For
              </label>
              <select
                value={settings.suppressPopupDays}
                onChange={(e) => handleChange('suppressPopupDays', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value={0}>Always ask</option>
                <option value={1}>1 day</option>
                <option value={7}>1 week</option>
                <option value={30}>1 month</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Don't show popup for this duration after dismissing
              </p>
            </div>
          )}

          {/* Default View */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Default View
            </label>
            <select
              value={settings.defaultView}
              onChange={(e) => handleChange('defaultView', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="month">Month</option>
              <option value="week">Week</option>
              <option value="day">Day</option>
            </select>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">To-Do List Settings</h3>
          </div>

          {/* Show Todo List */}
          <div>
            <label className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900">Show To-Do List on Login</p>
                <p className="text-xs text-gray-500">Display daily to-do list popup when logging in</p>
              </div>
              <input
                type="checkbox"
                checked={settings.showTodoList}
                onChange={(e) => handleChange('showTodoList', e.target.checked)}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
            </label>
          </div>

          {/* Todo Days Threshold */}
          {settings.showTodoList && (
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Days Threshold for To-Do List
              </label>
              <select
                value={settings.todoDaysThreshold}
                onChange={(e) => handleChange('todoDaysThreshold', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                <option value={30}>30 days</option>
                <option value={60}>60 days</option>
                <option value={90}>90 days</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Show items due within this number of days
              </p>
            </div>
          )}

          {/* Don't Show Today Option */}
          {settings.showTodoList && (
            <div>
              <label className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-900">Allow "Don't Show Today"</p>
                  <p className="text-xs text-gray-500">Show option to dismiss popup for the day</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.dontShowToday}
                  onChange={(e) => handleChange('dontShowToday', e.target.checked)}
                  className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                />
              </label>
            </div>
          )}

          <div className="flex items-center justify-end pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
